import {
    useCallback,
    useEffect,
    useState,
    useMemo,
    type FC,
    useRef,
    useId
} from "react";
import {
    KeyboardAvoidingView,
    useWindowDimensions,
    Platform,
    View
} from "react-native";
import type ICommandDialogProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    IModalRef
} from "../../../modal/type";
import {
    getIsCommandShortcutClaimed,
    getIsCommandShortcutPressed
} from "../../util";
import {
    warnOnce
} from "../../../../utils/development";
import {
    CommandDialogContext
} from "../../context";
import {
    getKbdPlatform
} from "../../../kbd/util";
import {
    uuid
} from "../../../../utils";
import Modal from "../../../modal";

const CommandDialog: FC<ICommandDialogProps> = ({
    globalShortcut = "mod+K",
    isDefaultOpen = false,
    isOpen: isOpenProp,
    variant = "auto",
    customLocalize,
    onOverlayPress,
    onOpenRequest,
    onOpenChange,
    customTheme,
    id: outID,
    isVisible,
    children,
    onClosed,
    style,
    title
}) => {
    const {
        shapeConfigs,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        height: windowHeight
    } = useWindowDimensions();

    const insets = useSafeAreaInsets();

    const pointerType = usePointerType();

    const baseId = useId();

    const id = useRef(outID ? outID : uuid());

    const modalRef = useRef<IModalRef>(null);

    const [
        isOpenState,
        setIsOpenState
    ] = useState(isDefaultOpen);

    const controlledOpen = isOpenProp ?? isVisible;

    const isControlled = controlledOpen !== undefined;

    const isOpen = isControlled ? controlledOpen : isOpenState;

    const [
        isMounted,
        setIsMounted
    ] = useState(isOpen);

    const isOpenRef = useRef(isOpen);

    const isControlledRef = useRef(isControlled);

    const onOpenChangeRef = useRef(onOpenChange);

    const onOpenRequestRef = useRef(onOpenRequest);

    const isSheet = variant === "sheet" || (variant === "auto" && pointerType === "coarse");

    const instructionsId = `${baseId}-instructions`;

    const {
        gutter: gutterDynamicStyle,
        sheet: sheetDynamicStyle
    } = useStyles({
        shapeConfigs,
        windowHeight,
        colors,
        insets,
        spaces
    });

    useEffect(() => {
        isOpenRef.current = isOpen;

        isControlledRef.current = isControlled;

        onOpenChangeRef.current = onOpenChange;

        onOpenRequestRef.current = onOpenRequest;
    });

    useEffect(() => {
        if(isVisible !== undefined) {
            warnOnce({
                message: "CommandDialog: \"isVisible\" is deprecated and will be removed in the next major version. Use \"isOpen\" instead."
            });
        }

        if(onOpenRequest) {
            warnOnce({
                message: "CommandDialog: \"onOpenRequest\" is deprecated and will be removed in the next major version. Use \"onOpenChange\" instead."
            });
        }
    }, [
        onOpenRequest,
        isVisible
    ]);

    useEffect(() => {
        if(isOpen) {
            setIsMounted(true);
        }
    }, [
        isOpen
    ]);

    const handleOpenChange = useCallback((nextOpen: boolean) => {
        if(onOpenChangeRef.current) onOpenChangeRef.current(nextOpen);

        if(nextOpen && onOpenRequestRef.current) onOpenRequestRef.current();

        if(!isControlledRef.current) {
            setIsOpenState(nextOpen);
        }
    }, []);

    const handleModalClosed = useCallback((props: {
        id: string;
    }) => {
        setIsMounted(false);

        if(!isControlledRef.current) {
            setIsOpenState(false);
        }

        if(onClosed) onClosed(props);
    }, [
        onClosed
    ]);

    useEffect(() => {
        if(Platform.OS !== "web" || typeof document === "undefined" || !globalShortcut) {
            return;
        }

        const platform = getKbdPlatform();

        const onKeyDown = (event: KeyboardEvent) => {
            if(event.repeat || getIsCommandShortcutClaimed({
                shortcut: globalShortcut,
                target: event.target
            }) || !getIsCommandShortcutPressed({
                shortcut: globalShortcut,
                platform,
                event
            })) {
                return;
            }

            event.preventDefault();

            if(isOpenRef.current) {
                handleOpenChange(false);

                modalRef.current?.closeAnimation();

                return;
            }

            handleOpenChange(true);
        };

        document.addEventListener("keydown", onKeyDown, true);

        return () => {
            document.removeEventListener("keydown", onKeyDown, true);
        };
    }, [
        handleOpenChange,
        globalShortcut
    ]);

    const handleClose = useCallback(() => {
        handleOpenChange(false);

        modalRef.current?.closeAnimation();
    }, [
        handleOpenChange
    ]);

    const dialogContext = useMemo(() => ({
        close: handleClose,
        instructionsId,
        isSheet
    }), [
        instructionsId,
        handleClose,
        isSheet
    ]);

    const renderContent = () => {
        if(isSheet) {
            return <KeyboardAvoidingView
                behavior={Platform.OS === "ios" ? "padding" : undefined}
                style={[
                    style,
                    stylesheet.sheet,
                    sheetDynamicStyle
                ]}
            >
                {children}
            </KeyboardAvoidingView>;
        }

        return <View
            style={[
                style,
                stylesheet.gutter,
                gutterDynamicStyle
            ]}
        >
            {children}
        </View>;
    };

    if(!isOpen && !isMounted) {
        return null;
    }

    return <Modal
        aria-label={title ?? localize("command-palette")}
        aria-describedby={instructionsId}
        customLocalize={customLocalize}
        onOverlayPress={({
            closeAnimation
        }) => {
            if(onOverlayPress) {
                onOverlayPress({
                    closeAnimation
                });

                return;
            }

            handleOpenChange(false);

            closeAnimation();
        }}
        onClosed={handleModalClosed}
        isOverlayVisible={!isSheet}
        customTheme={customTheme}
        isShowSurface={false}
        isShowClose={false}
        alignContent="free"
        isOpen={isOpen}
        id={id.current}
        ref={modalRef}
        role="dialog"
    >
        <CommandDialogContext.Provider
            value={dialogContext}
        >
            {renderContent()}
        </CommandDialogContext.Provider>
    </Modal>;
};
export default CommandDialog;
